import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import FormatDateTime from "../../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";

// Define a style object for table cells with reduced padding and font size
const tableCellStyle = {
  border: "1px solid #ddd",
  textAlign: "center",
  padding: "4px",
  fontSize: "0.8rem",
};

// Function to return row background color based on status
const getRowColor = (status) => {
  // Adjust these values as needed for each status
  if (status === 1) return "#FFF3CD"; // e.g. Pending (light yellow)
  if (status === 2) return "#D1ECF1"; // e.g. In Progress (light blue)
  if (status === 3) return "#D4EDDA"; // e.g. Completed (light green)
  // add more cases if needed
  return "transparent"; // default color
};

const HtPatientView = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  // Local states
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);

  // Filters
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalProcedure, setTotalProcedure] = useState(0);

  // Fetching data
  const {
    data: procedures,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["ht-patients", page, rowsPerPage, keyword, type, status, date],
    `ht-patients/?search=${keyword}&type=${type}&status=${status}&procedure_date=${date}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  // Deletion hook
  const { handleDelete, ConfirmDialog } = useDelete("ht-patients", token);

  // Effects
  useEffect(() => {
    refetch();
  }, [keyword, type, status, date, page, rowsPerPage, refetch]);

  useEffect(() => {
    if (procedures && procedures.count !== undefined) {
      setTotalProcedure(procedures.count);
    }
  }, [procedures]);

  // Handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleOpenDialog = () => {
    setOpen(!open);
  };

  // Prepare export columns and data for PDF export (if used)
  const exportColumns = [
    { label: t("call_center.id"), id: "id" },
    { label: t("call_center.name"), id: "full_name" },
    { label: t("call_center.contact"), id: "contact" },
    { label: t("call_center.procedure"), id: "procedure_name" },
    { label: t("call_center.procedureDate"), id: "procedure_date" },
    { label: t("call_center.status"), id: "status" },
    { label: t("call_center.price"), id: "price" },
    { label: t("call_center.by"), id: "by" },
    { label: t("call_center.description"), id: "description" },
    { label: t("call_center.advance"), id: "advance" },
    { label: t("call_center.zone"), id: "zone" },
  ];

  const exportData = procedures?.results?.map((client) => ({
    id: client.id,
    full_name: client.full_name,
    contact: client.contact,
    procedure_name: client?.display_procedure?.name,
    procedure_date: client?.procedure_date,
    status: client?.status_info?.name,
    price: client.price,
    by: `${client?.display_user?.first_name} ${client?.display_user?.last_name}`,
    description: client?.description || "",
    advance: client.advance || "",
    zone: client.zone || "",
  }));

  // Helper function to determine row background color based on status
  const getRowBackgroundColor = (procedure) => {
    // Assuming procedure.status holds a numeric value
    const statusValue = procedure.status;
    const statusColors = {
      1: "#d4edda", // done (greenish)
      2: "#f8d7da", // cancel (light red)
      3: "#d1ecf1", // followUP (light blue)
      4: "#fff3cd", // inProgress (yellowish)
      5: "#e2e3e5", // dontWant (grayish)
      6: "#e2d6f9", // nextDate (lavender)
      7: "#fff9c4", // pending (light yellow)
      8: "#d0f0c0", // fixed (light green)
      9: "#bbdefb", // flexible (light blue)
      10: "#cfd8dc", // noAnswer (blue gray)
      11: "#dcedc8", // information (pale green)
      12: "#fce4ec", // comingSoon (pinkish)
    };
    return statusColors[statusValue] || "transparent";
  };

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 2, width: "100%", borderRadius: 2, boxShadow: 3 }}>
        {/* Page Title */}
        <Typography
          variant="h5"
          fontWeight="bold"
          align="center"
          sx={{ pb: 2, borderBottom: "5px solid #007471" }}
        >
          {t("call_center.htPatientList")}
        </Typography>

        {/* Filters Section */}
        <Box mt={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            {/* Search Input */}
            <TextField
              type="search"
              size="small"
              fullWidth
              label={t("call_center.search")}
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Date Filter */}
            <TextField
              type="date"
              size="small"
              label={t("call_center.filterByDate")}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setPage(1);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            {/* Type Filter */}
            <TextField
              select
              size="small"
              label={t("call_center.filterByType")}
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 200 }}
            >
              {isLoadingType && (
                <Box display="flex" justifyContent="center" p={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {isErrorType && (
                <MenuItem value="">{t("call_center.errorField")}</MenuItem>
              )}
              <MenuItem value="">{t("call_center.all")}</MenuItem>
              {types?.map((typeItem) => (
                <MenuItem value={typeItem.id} key={typeItem.id}>
                  {typeItem.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Status Filter */}
            <TextField
              select
              size="small"
              label={t("call_center.filterByStatus")}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t("call_center.all")}</MenuItem>
              <MenuItem value={1}>{t("call_center.done")}</MenuItem>
              <MenuItem value={2}>{t("call_center.cancel")}</MenuItem>
              <MenuItem value={3}>{t("call_center.followUP")}</MenuItem>
              <MenuItem value={4}>{t("call_center.inProgress")}</MenuItem>
              <MenuItem value={5}>{t("call_center.dontWant")}</MenuItem>
              <MenuItem value={6}>{t("call_center.nextDate")}</MenuItem>
              <MenuItem value={7}>{t("call_center.pending")}</MenuItem>
              <MenuItem value={8}>{t("call_center.fixed")}</MenuItem>
              <MenuItem value={9}>{t("call_center.flexible")}</MenuItem>
              <MenuItem value={10}>{t("call_center.noAnswer")}</MenuItem>
              <MenuItem value={11}>{t("call_center.information")}</MenuItem>
              <MenuItem value={12}>{t("call_center.comingSoon")}</MenuItem>
            </TextField>
          </Stack>
        </Box>

        {/* Table Section */}
        <TableContainer sx={{ mt: 3 }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={tableCellStyle}>{t("call_center.id")}</TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.patient")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.contact")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.procedureType")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.date")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.period")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.doctors")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.totalCost")}
                </TableCell>
                {/* New Advance Column */}
                <TableCell sx={tableCellStyle}>
                  {t("call_center.advance")}
                </TableCell>
                {/* New Zone Column */}
                <TableCell sx={tableCellStyle}>
                  {t("call_center.zone")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.status")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.lab")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.price")}
                </TableCell>
                <TableCell sx={tableCellStyle}>
                  {t("call_center.nextVisitDate")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {procedures?.results?.map((procedure) => (
                <TableRow
                  key={procedure.id}
                  hover
                  style={{
                    backgroundColor: getRowBackgroundColor(procedure),
                  }}
                >
                  <TableCell sx={tableCellStyle}>{procedure.id}</TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {procedure.patient_name}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {procedure?.patient_details?.phone}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {procedure.display_procedure_type}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {procedure?.procedure_date
                      ? FormatDateTime(procedure.procedure_date)
                      : ""}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {procedure?.display_period}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {procedure?.display_doctors?.length > 0 ? (
                      procedure.display_doctors.map((doc, idx) => (
                        <Chip
                          key={idx}
                          label={`${doc.first_name} ${doc.last_name}`}
                          sx={{ mr: 0.5, mb: 0.5 }}
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Chip
                        label={t("call_center.noDoctorData")}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {procedure.total_cost || ""}
                  </TableCell>
                  {/* Advance Column */}
                  <TableCell sx={tableCellStyle}>
                    {procedure.advance || ""}
                  </TableCell>
                  {/* Zone Column */}
                  <TableCell sx={tableCellStyle}>
                    {procedure.zone || ""}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    <Chip
                      sx={{ fontWeight: "bold", color: "blue" }}
                      label={procedure?.status_name}
                    />
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {procedure?.display_lab?.length > 0 ? (
                      procedure.display_lab.map((lab, idx) => (
                        <Chip
                          key={idx}
                          label={lab}
                          sx={{ mr: 0.5, mb: 0.5 }}
                          variant="outlined"
                        />
                      ))
                    ) : (
                      <Chip
                        label={t("call_center.noLabData")}
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                    {procedure.total_cost}{" "}
                    {(procedure.currency === 1 && "؋") ||
                      (procedure.currency === 2 && "$") ||
                      (procedure.currency === 3 && "€")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {procedure?.next_visit_date
                      ? FormatDateTime(procedure.next_visit_date)
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        <Box
          mt={2}
          mb={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{t("call_center.rowsPerPage")}:</Typography>
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={Math.ceil(totalProcedure / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Box>
      </Paper>
    </Grid>
  );
};

export default HtPatientView;
